import { useState } from "react";
import { RefreshCw, Trash2, CheckCircle, XCircle, Archive, User, Mail, Phone, MessageSquare } from "lucide-react";
import api from "../../../api";
import { motion, AnimatePresence } from "framer-motion";
import { useNotification } from "../../../context/NotificationContext";
import Tooltip from "../../../components/ui/Tooltip";

export default function ArchiveGrid({ leads, onRestore, onDelete, open, onClose }) {
  const [confirmRestore, setConfirmRestore] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const { showToast } = useNotification();

  const handleRestore = async () => {
    if (!confirmRestore) return;
    try {
      await onRestore(confirmRestore.id || confirmRestore._id);
    } catch (error) {
      console.error("Erreur lors de la restauration :", error);
      showToast("Erreur lors de la restauration", "error");
    }
    setConfirmRestore(null);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await onDelete(confirmDelete.id || confirmDelete._id);
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      showToast("Erreur lors de la suppression", "error");
    }
    setConfirmDelete(null);
  };

  const filteredLeads = leads.filter((lead) => lead.status === "DISQUALIFIED");

  return (
    <div className="flex flex-col flex-1 min-h-0 w-full bg-surface overflow-hidden">
      <div className="overflow-auto flex-1 min-h-0">
        <table className="w-full text-left border-collapse whitespace-nowrap min-w-[800px]">
          <thead className="bg-surface-muted border-b border-workspace sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="px-6 py-4 text-xs uppercase tracking-wider font-semibold text-subtle">Nom complet</th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider font-semibold text-subtle">Email</th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider font-semibold text-subtle">Téléphone</th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider font-semibold text-subtle">Source</th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider font-semibold text-subtle">Commentaires</th>
              <th className="px-6 py-4 text-xs uppercase tracking-wider font-semibold text-subtle text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-workspace">
            {filteredLeads.map((lead) => (
              <tr 
                key={lead.id || lead._id} 
                className="group transition-colors hover:bg-ink-50"
              >
                <td className="px-6 py-4 text-sm font-semibold text-primary">
                  {lead.lastName} {lead.firstName}
                </td>
                <td className="px-6 py-4 text-sm text-muted">{lead.email || "-"}</td>
                <td className="px-6 py-4 text-sm text-muted">{lead.phone || "-"}</td>
                <td className="px-6 py-4 text-sm text-muted">{lead.source || "-"}</td>
                <td className="px-6 py-4 text-sm text-muted max-w-[200px] truncate">
                  {lead.interestNotes ? (
                    <Tooltip content={lead.interestNotes}>
                      <span>{lead.interestNotes}</span>
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Tooltip content="Restaurer">
                      <button
                        className="btn btn-secondary !px-3 !py-1 !min-h-[2rem] text-success-700 hover:bg-success-50 hover:border-success-200"
                        onClick={() => setConfirmRestore(lead)}
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Restaurer
                      </button>
                    </Tooltip>
                    <Tooltip content="Supprimer définitivement">
                      <button
                        className="btn btn-secondary !px-3 !py-1 !min-h-[2rem] text-danger-700 hover:bg-danger-50 hover:border-danger-200"
                        onClick={() => setConfirmDelete(lead)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            ))}
            
            {filteredLeads.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-32 border-b-0">
                  <div className="flex flex-col items-center justify-center text-center">
                    <motion.div
                      animate={{ y: [0, -10, 0] }}
                      transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                    >
                      <Archive className="w-16 h-16 text-tertiary mb-6 mx-auto opacity-50" strokeWidth={1.5} />
                    </motion.div>
                    <p className="text-subtle whitespace-normal font-medium">
                      Aucun lead archivé trouvé.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {confirmRestore && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.4)] backdrop-blur-sm z-[60] p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="workspace-card-elevated p-8 w-full max-w-sm flex flex-col items-center text-center rounded-2xl"
              initial={{ scale: 0.9, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="w-16 h-16 rounded-full bg-success-100 text-success-600 flex items-center justify-center mb-6 shadow-sm">
                <RefreshCw className="w-8 h-8" />
              </div>
              <h2 className="text-xl font-bold text-primary mb-2">Restaurer ce lead ?</h2>
              <p className="text-subtle text-sm mb-8">
                Voulez-vous restaurer <strong>{confirmRestore.lastName} {confirmRestore.firstName}</strong> dans la liste active ?
              </p>
              <div className="flex items-center gap-3 w-full">
                <button
                  className="btn btn-secondary flex-1"
                  onClick={() => setConfirmRestore(null)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-success flex-1 text-white bg-success-600 border-success-600 hover:bg-success-700 hover:border-success-700"
                  onClick={handleRestore}
                >
                  Restaurer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmDelete && (
          <motion.div
            className="fixed inset-0 flex items-center justify-center bg-[rgb(var(--hp-ink-950-rgb)/0.4)] backdrop-blur-sm z-[60] p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="workspace-card-elevated p-8 w-full max-w-sm flex flex-col items-center text-center rounded-2xl"
              initial={{ scale: 0.9, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 10 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="w-16 h-16 rounded-full bg-danger-100 text-danger-600 flex items-center justify-center mb-6 shadow-sm">
                <Trash2 className="w-8 h-8" />
              </div>
              <h2 className="text-xl font-bold text-primary mb-2">Supprimer définitivement ?</h2>
              <p className="text-subtle text-sm mb-8">
                Attention, cette action est irréversible. Voulez-vous supprimer <strong>{confirmDelete.lastName} {confirmDelete.firstName}</strong> ?
              </p>
              <div className="flex items-center gap-3 w-full">
                <button
                  className="btn btn-secondary flex-1"
                  onClick={() => setConfirmDelete(null)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-danger flex-1"
                  onClick={handleDelete}
                >
                  Supprimer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
